import { type KeyboardEvent } from "react";

import { useAsyncSubmit } from "ui/button/loading-button/use-async-submit";
import { shouldConfirmOnEnter } from "ui/dialog/dialog.helpers";

import { type ConfirmationDialogProps } from "ui/confirmation-dialog/confirmation-dialog.types";

type UseConfirmationDialogOptions = Pick<ConfirmationDialogProps, "onConfirm" | "onKeyDown"> & {
  confirming: boolean;
  disabled: boolean;
  confirmOnEnter: boolean;
};

export type ConfirmationDialogState = {
  /** `confirming` from the caller, or an async `onConfirm` still running. */
  isPending: boolean;
  handleConfirm: () => void;
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
};

export const useConfirmationDialog = ({
  onConfirm,
  onKeyDown,
  confirming,
  disabled,
  confirmOnEnter,
}: UseConfirmationDialogOptions): ConfirmationDialogState => {
  const { submit, isSubmitting } = useAsyncSubmit(async () => await onConfirm(), {
    onError: (error) => console.error("An error occurred:", error),
  });

  const isPending = confirming || isSubmitting;

  /* `useAsyncSubmit` rethrows after `onError` has logged; the rejection is already handled. */
  const handleConfirm = () => {
    void submit().catch(() => undefined);
  };

  /* The caller's handler runs first, so its `preventDefault()` keeps Enter from confirming. */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);

    if (shouldConfirmOnEnter(event, { enabled: confirmOnEnter, blocked: disabled || isPending })) {
      handleConfirm();
    }
  };

  return { isPending, handleConfirm, handleKeyDown };
};
